import type { GetServerSidePropsContext } from 'next'
import type { ParsedUrlQuery } from 'querystring'
import type { DaDataAddress } from 'react-dadata'

export const LOCALITY_COOKIE = 'navigatorLocality'
export const DADATA_TOKEN = 'd0f1dba8edb62a9900ac190e3a50e3da54654b52'

export interface Locality {
  localityGUID: string
  name: string
  lat?: number
  lon?: number
}

export const isLocalityRoute = (pathname: string) =>
  ['', '/programs', '/events', '/units', '/map', '/search'].includes(
    pathname.replace(/\/$/, ''),
  )

const first = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value

export const validGUID = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(value)

export const parseLocality = (raw?: string): Locality | null => {
  try {
    const value = JSON.parse(raw || 'null')
    if (validGUID(value?.localityGUID) && typeof value.name === 'string') {
      return {
        localityGUID: value.localityGUID,
        name: value.name.slice(0, 200),
        ...(Number.isFinite(value.lat) &&
        Number.isFinite(value.lon) &&
        Math.abs(value.lat) <= 90 &&
        Math.abs(value.lon) <= 180
          ? { lat: value.lat, lon: value.lon }
          : {}),
      }
    }
  } catch {
    // Старые настройки содержали идентификатор муниципалитета вместо города.
  }
  return null
}

export const localityFromAddress = (data: DaDataAddress): Locality | null => {
  if (data.region_kladr_id && !data.region_kladr_id.startsWith('61'))
    return null
  const localityGUID = data.settlement_fias_id || data.city_fias_id
  const name = data.settlement_with_type || data.city_with_type
  if (!validGUID(localityGUID) || !name) return null
  const lat = Number.parseFloat(data.geo_lat || '')
  const lon = Number.parseFloat(data.geo_lon || '')
  return {
    localityGUID,
    name,
    ...(Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : {}),
  }
}

export const localityQuery = (locality: Locality | null) => ({
  // Пустое значение явно означает всю область, без адреса из Django-сессии.
  localityGUID: locality?.localityGUID || '',
  localityName: locality?.name || '',
})

export const locationChangeQuery = (
  query: ParsedUrlQuery,
  locality: Locality | null,
) => {
  const next = { ...query }
  // Фильтры района от предыдущего города не должны скрывать новую выдачу.
  ;['mo_id', 'municipalityDistrict', 'parentGUID', 'parentID', 'is_mo'].forEach(
    (key) => delete next[key],
  )
  return { ...next, ...localityQuery(locality), page: '1' }
}

export const localityFromQuery = (
  query: ParsedUrlQuery,
  saved: Locality | null,
): Locality | null => {
  const guid = first(query.localityGUID)
  if (!validGUID(guid)) return null
  return {
    ...(saved?.localityGUID === guid ? saved : {}),
    localityGUID: guid,
    name:
      first(query.localityName) ||
      (saved?.localityGUID === guid
        ? saved.name
        : 'Выбранный населённый пункт'),
  }
}

/** Канонический URL хранит выбранный город; cookie применяется при переходах. */
export const catalogueRequest = ({
  query,
  req,
  resolvedUrl,
}: Pick<GetServerSidePropsContext, 'query' | 'req' | 'resolvedUrl'>) => {
  const saved = parseLocality(req.cookies[LOCALITY_COOKIE])
  const explicit = Object.prototype.hasOwnProperty.call(query, 'localityGUID')
  if (!explicit && saved) {
    const params = new URLSearchParams(resolvedUrl.split('?')[1] || '')
    Object.entries(localityQuery(saved)).forEach(([key, value]) =>
      params.set(key, value),
    )
    return {
      redirect: {
        destination: `${resolvedUrl.split('?')[0]}?${params}`,
        permanent: false as const,
      },
      params: {},
    }
  }
  const params: Record<string, string> = {}
  Object.entries(query).forEach(([key, value]) => {
    const scalar = first(value)
    if (key !== 'localityName' && scalar !== undefined) params[key] = scalar
  })
  params.localityGUID = validGUID(params.localityGUID)
    ? params.localityGUID
    : ''
  return { params }
}
